/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DocumentAutomationSummary } from '../models/DocumentAutomationSummary';
import type { WorkflowCreatePayload } from '../models/WorkflowCreatePayload';
import type { WorkflowPausePayload } from '../models/WorkflowPausePayload';
import type { WorkflowPauseWithRecords } from '../models/WorkflowPauseWithRecords';
import type { WorkflowStartPayload } from '../models/WorkflowStartPayload';
import type { WorkflowSummary } from '../models/WorkflowSummary';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class WorkflowsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create Workflow For Namespace
     * @param namespaceId
     * @param requestBody
     * @returns WorkflowCreatePayload Successful Response
     * @throws ApiError
     */
    public createWorkflowForNamespace(
        namespaceId: string,
        requestBody: WorkflowCreatePayload,
    ): CancelablePromise<WorkflowCreatePayload> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/workflows/create-workflow-for-namespace',
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Workflow
     * @param workflowId
     * @param namespaceId
     * @param requestBody
     * @returns WorkflowCreatePayload Successful Response
     * @throws ApiError
     */
    public updateWorkflow(
        workflowId: string,
        namespaceId: string,
        requestBody: WorkflowCreatePayload,
    ): CancelablePromise<WorkflowCreatePayload> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/workflows/update-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Workflow
     * @param workflowId
     * @returns WorkflowCreatePayload Successful Response
     * @throws ApiError
     */
    public getWorkflow(
        workflowId: string,
    ): CancelablePromise<WorkflowCreatePayload> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/get-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Workflows For Namespace
     * @param namespaceId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listWorkflowsForNamespace(
        namespaceId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/list-workflows-for-namespace',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Workflows For Entities In Namespace
     * get list of workflows actually at the entity level for the copy from feature
     * @param clusterId
     * @param namespaceId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listWorkflowsForEntitiesInNamespace(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/list-workflows-for-entities-in-namespace',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Workflow
     * @param workflowId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteWorkflow(
        workflowId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/workflows/delete-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Notification Workflow Summaries For Membership
     * @param membershipId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listNotificationWorkflowSummariesForMembership(
        membershipId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/list-notification-workflow-summaries-for-membership',
            query: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Notification Workflow Summaries For Namespace
     * @param namespaceId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listNotificationWorkflowSummariesForNamespace(
        namespaceId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/list-notification-workflow-summaries-for-namespace',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Notification Workflow Summaries For Entity
     * @param entityId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listNotificationWorkflowSummariesForEntity(
        entityId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/list-notification-workflow-summaries-for-entity',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Pause Workflow
     * Pause a workflow by its ID.
     *
     * Args:
     * workflow_id: The ID of the workflow to pause
     * paused_until: Optional date until which the workflow should be paused
     *
     * Returns:
     * None
     * @param workflowId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public pauseWorkflow(
        workflowId: string,
        requestBody: WorkflowPausePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/workflows/pause-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Resume Workflow
     * @param workflowId
     * @returns any Successful Response
     * @throws ApiError
     */
    public resumeWorkflow(
        workflowId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/workflows/resume-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Workflow Pause History
     * Returns all WorkflowPause for the workflow, each with its associated WorkflowPauseRecords.
     * @param workflowId
     * @returns WorkflowPauseWithRecords Successful Response
     * @throws ApiError
     */
    public listWorkflowPauseHistory(
        workflowId: string,
    ): CancelablePromise<Array<WorkflowPauseWithRecords>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/workflows/workflows/{workflow_id}/pause-history',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Scheduled Workflow
     * Start a scheduled automation by its ID as of a specific date.
     *
     * Args:
     * workflow_id: The ID of the scheduled automation to start
     * payload: Payload containing next_run_at date
     *
     * Returns:
     * None
     * @param workflowId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public startScheduledWorkflow(
        workflowId: string,
        requestBody: WorkflowStartPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/workflows/start-scheduled-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Manually Initiated Workflow
     * Start a manually initiated automation by its ID as of a specific date.
     *
     * Args:
     * workflow_id: The ID of the manually initiated automation to start
     * payload: Payload containing next_run_at date
     *
     * Returns:
     * None
     * @param workflowId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public startManuallyInitiatedWorkflow(
        workflowId: string,
        requestBody: WorkflowStartPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/workflows/start-manually-initiated-workflow/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Workflow For Entity
     * @param entityId
     * @param requestBody
     * @returns WorkflowCreatePayload Successful Response
     * @throws ApiError
     */
    public createWorkflowForEntity(
        entityId: string,
        requestBody: WorkflowCreatePayload,
    ): CancelablePromise<WorkflowCreatePayload> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/workflows/create-workflow-for-entity',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Workflows For Entity
     * @param entityId
     * @returns WorkflowSummary Successful Response
     * @throws ApiError
     */
    public listWorkflowsForEntity(
        entityId: string,
    ): CancelablePromise<Array<WorkflowSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/workflows/list-workflows-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Document Automation Summaries For Entity
     * @param entityId
     * @returns DocumentAutomationSummary Successful Response
     * @throws ApiError
     */
    public listDocumentAutomationSummariesForEntity(
        entityId: string,
    ): CancelablePromise<Array<DocumentAutomationSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/workflows/list-document-automation-summaries-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
